/**
 * Global exclusive ContentTooltip open store.
 * At most one tip portal is active app-wide; claim B dismisses A.
 * Shared morph `groupId`s use instance ownership so badge hops keep one portal.
 * Independent of DropdownMenu exclusivity ({@link exclusive-overlay}).
 */

type Listener = () => void;

let activeTooltipId: string | null = null;
let activeInstanceId: string | null = null;
const listeners = new Set<Listener>();

function emit(): void {
	for (const listener of listeners) {
		listener();
	}
}

export function getContentTooltipActiveId(): string | null {
	return activeTooltipId;
}

export function getContentTooltipActiveInstanceId(): string | null {
	return activeInstanceId;
}

export function subscribeContentTooltipActive(listener: Listener): () => void {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
}

/**
 * Claim exclusive tip ownership. Previous active id/instance is cleared.
 * `@param instanceId` — tip instance within a shared morph group (defaults to `id`).
 */
export function claimContentTooltip(id: string, instanceId: string = id): void {
	if (activeTooltipId === id && activeInstanceId === instanceId) {
		return;
	}
	activeTooltipId = id;
	activeInstanceId = instanceId;
	emit();
}

/** Release if this id+instance still owns the tip slot. */
export function releaseContentTooltip(
	id: string,
	instanceId: string = id,
): void {
	if (activeTooltipId !== id || activeInstanceId !== instanceId) {
		return;
	}
	activeTooltipId = null;
	activeInstanceId = null;
	emit();
}

/** Test helper — reset singleton between vitest cases. */
export function resetContentTooltipExclusiveStoreForTests(): void {
	activeTooltipId = null;
	activeInstanceId = null;
	listeners.clear();
}

/** Force-clear tip slot (e.g. DnD drag start). Does not affect DropdownMenu exclusivity. */
export function clearContentTooltipExclusive(): void {
	if (activeTooltipId == null && activeInstanceId == null) {
		return;
	}
	activeTooltipId = null;
	activeInstanceId = null;
	emit();
}
